import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Grid,
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  styled,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from "formik";
import { Navigate, useNavigate } from "react-router-dom";
import BudgieLogo from "../../assets/images/logo/Budgie_Logo.png";
import MicrosoftLogo from "../../assets/images/logo/Microsoft logo.png";
import WarningLogo from "../../assets/images/login/warning.png";
import Cover from "@/assets/images/login/LineVector.svg";
import * as Yup from "yup";
import Footer from "./Footer";
import {
  useLoginEmployeeMutation,
  useMicrosoftLoginMutation,
  useRefreshTokenCheckMutation,
  useGoogleLoginMutation,
} from "@/api/services/auth/login/LoginApi";
import axios from "axios";
import { toast } from "@/components/toastify";
import { useDispatch } from "react-redux";
import { setAuthData } from "@/slices/slices_/employeeSlice";
import { scheduleProactiveRefresh } from "@/utils/authUtils";
import { ArrowBackIosIcon } from "@/icons";
import { setAESKey } from "@/api/aesKeySlice";
import { useForgotPasswordMailMutation } from "@/api/services/auth/login/LoginApi";
import SubmitButtonWithLoader from "@/components/forms/SubmitButtonWithLoader";
import Loader from "@/components/loader";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren",
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const slideVariants = {
  hidden: { x: 50, opacity: 0 },
  visible: { x: 0, opacity: 1 },
  exit: { x: -50, opacity: 0 },
};

const GradientBackground = styled(Box)(({ theme }) => ({
  background: theme.palette.customStyles.gradientBackground,
  boxShadow: theme.shadows[2],
}));

let sessionAESKey = null;

// Utility Functions
const parseMicrosoftUserFormat = (str) => {
  const inner = str.replace(/^UserResponseDetails\(|\)$/g, "");
  const keyValuePairs = inner.split(/,(?![^\[]*\])/g);
  const result = {};

  keyValuePairs.forEach((pair) => {
    const [key, rawValue] = pair.split("=");
    const value = rawValue?.trim();

    if (!key) return;

    if (value === "null") {
      result[key.trim()] = null;
    } else if (value === "true" || value === "false") {
      result[key.trim()] = value === "true";
    } else if (/^\[.*\]$/.test(value)) {
      result[key.trim()] = value
        .slice(1, -1)
        .split(",")
        .map((v) => v.trim());
    } else if (/^\d+$/.test(value)) {
      result[key.trim()] = Number(value);
    } else {
      result[key.trim()] = value;
    }
  });

  return result;
};

async function decryptAES(encryptedBase64) {
  const encryptedData = base64ToUint8Array(encryptedBase64);
  const iv = encryptedData.slice(0, 12);
  const ciphertext = encryptedData.slice(12);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    sessionAESKey,
    ciphertext,
  );

  return new TextDecoder().decode(decryptedBuffer);
}

function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function base64ToUint8Array(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let b of bytes) binary += String.fromCharCode(b);
  return window.btoa(binary);
}

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "975094680445-jomdnqhrvq3foc0kb23tu981jq9fvgfb.apps.googleusercontent.com";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordMail, { isLoading: mailLoading }] =
    useForgotPasswordMailMutation();
  const [microSoftLogin, { isLoading: loader }] = useMicrosoftLoginMutation();
  const [googleLogin] = useGoogleLoginMutation();
  const googleCallbackRef = useRef(null);
  const [message, setMessage] = useState("Loading...");
  const [EncryptedData, setEncryptedData] = useState("");
  const [isMicrosoftLogin, setIsMicrosoftLogin] = useState(false);
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [refreshTokenCheck, { isLoading: isRefreshing }] =
    useRefreshTokenCheckMutation();

  useEffect(() => {
    setLoadingData(true);

    const hash = window.location.hash;
    const search = window.location.search;

    let queryString = "";
    if (hash.includes("?")) {
      queryString = hash.split("?")[1];
    } else if (search) {
      queryString = search.substring(1);
    }

    const params = new URLSearchParams(queryString);
    const token = params.get("token");
    const message = params.get("message");

    if (message === "unauthorized") {
      toast("error", "You do not have permission to log in.");
      navigate("/login");
      setLoadingData(false);
      return;
    }

    const provider = params.get("provider");

    if (token) {
      (async () => {
        const isGoogle = provider === "google";
        isGoogle ? setIsGoogleLogin(true) : setIsMicrosoftLogin(true);
        try {
          const loginFn = isGoogle ? googleLogin : microSoftLogin;
          const response = await loginFn({
            authCredentials: { token },
          }).unwrap();

          localStorage.setItem("loginDetails", JSON.stringify(response));
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("microsoftLogin", "true");
          if (!isGoogle) localStorage.setItem("isMicrosoftAuthencated", "true");

          const localStorageData = JSON.parse(
            localStorage.getItem("loginDetails"),
          );

          dispatch(
            setAuthData({
              token: localStorageData.token,
              refreshToken: localStorageData.refreshToken,
              userDetails: localStorageData.userDetails,
            }),
          );
          toast("success", "Login successful");
          scheduleProactiveRefresh();

          const passwordUpdateStatus =
            localStorageData?.passwordUpdateStatus ??
            localStorageData?.userDetails?.passwordUpdateStatus;
          localStorage.setItem(
            "passwordUpdateStatus",
            String(passwordUpdateStatus),
          );

          if (
            passwordUpdateStatus === false ||
            passwordUpdateStatus === "false"
          ) {
            navigate("/changePasswordStatus");
          } else {
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Error handling login:", error);
          toast("error", error?.data?.message || "Login failed");
          localStorage.removeItem("loginDetails");
          localStorage.removeItem("isAuthenticated");
          navigate("/login");
        } finally {
          setIsMicrosoftLogin(false);
          setIsGoogleLogin(false);
        }
      })();
    } else {
      setLoadingData(false);
    }
  }, [navigate, dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_DEV_API_URL}crypto/public-key`,
        );

        const publicKey = res.data?.data?.publicKey;

        const rsaPublicKey = await window.crypto.subtle.importKey(
          "spki",
          base64ToArrayBuffer(publicKey),
          {
            name: "RSA-OAEP",
            hash: "SHA-256",
          },
          false,
          ["encrypt"],
        );

        const aesKey = await window.crypto.subtle.generateKey(
          {
            name: "AES-GCM",
            length: 256,
          },
          true,
          ["encrypt", "decrypt"],
        );

        sessionAESKey = aesKey;

        const rawAESKey = await window.crypto.subtle.exportKey("raw", aesKey);

        const encryptedAESKey = await window.crypto.subtle.encrypt(
          { name: "RSA-OAEP" },
          rsaPublicKey,
          rawAESKey,
        );
        // dispatch(
        //   setAESKey({
        //     rawKey: aesKey,
        //     encryptedKey: "ewkujfv",
        //   })
        // );
        setEncryptedData(encryptedAESKey);
      } catch (e) {
        setMessage("Error: " + e.message);
      }
    })();
  }, []);

  const handleMicrosoftLogin = () => {
    formik.resetForm();
    setIsMicrosoftLogin(true);
    try {
      const microsoftBaseUrl = process.env.REACT_APP_DEV_MS_LOGIN || "";
      if (!microsoftBaseUrl) {
        toast(
          "error",
          "Microsoft login is not configured. Please contact administrator.",
        );
        setIsMicrosoftLogin(false);
        return;
      }
      const state = Math.random().toString(36).substring(2, 15);
      const redirectUri = `${window.location.origin}/#/login`;
      window.location.href = `${microsoftBaseUrl}?redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
    } catch (error) {
      console.error("Failed to redirect for Microsoft login:", error);
      toast("error", "Failed to initiate Microsoft login. Please try again.");
      setIsMicrosoftLogin(false);
    }
  };

  const handleGoogleCredentialResponse = useCallback(
    async (response) => {
      setIsGoogleLogin(true);
      try {
        const data = await googleLogin({
          idToken: response.credential,
        }).unwrap();
        localStorage.setItem("loginDetails", JSON.stringify(data));
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("microsoftLogin", "true");
        const localStorageData = JSON.parse(
          localStorage.getItem("loginDetails"),
        );
        dispatch(
          setAuthData({
            token: localStorageData.token,
            refreshToken: localStorageData.refreshToken,
            userDetails: localStorageData.userDetails,
          }),
        );
        scheduleProactiveRefresh();
        const passwordUpdateStatus =
          localStorageData?.passwordUpdateStatus ??
          localStorageData?.userDetails?.passwordUpdateStatus;
        localStorage.setItem(
          "passwordUpdateStatus",
          String(passwordUpdateStatus),
        );
        toast("success", "Login successful");
        if (
          passwordUpdateStatus === false ||
          passwordUpdateStatus === "false"
        ) {
          navigate("/changePasswordStatus");
        } else {
          navigate("/dashboard");
        }
      } catch (error) {
        toast("error", error?.data?.message || "Google login failed");
      } finally {
        setIsGoogleLogin(false);
      }
    },
    [googleLogin, dispatch, navigate],
  );

  useEffect(() => {
    googleCallbackRef.current = handleGoogleCredentialResponse;
  }, [handleGoogleCredentialResponse]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || loadingData) return;
    const initGoogle = () => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => googleCallbackRef.current(response),
        ux_mode: "popup",
      });
      window.google.accounts.id.renderButton(
        document.getElementById("google-signin-btn"),
        { type: "standard", theme: "outline", size: "large", width: "100%" },
      );
    };
    if (window.google?.accounts) {
      initGoogle();
    } else {
      const existingScript = document.getElementById("google-gsi-script");
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = "google-gsi-script";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initGoogle;
        document.body.appendChild(script);
      } else {
        existingScript.onload = initGoogle;
      }
    }
  }, [loadingData]);

  const validationSchema = Yup.object().shape({
    employeeId: Yup.string().required("Employee ID is required"),
    password: Yup.string()
      .required("Password is required")
      .test(
        "invalid-password",
        "Password is Incorrect. Please check your password & try again",
        function (value) {
          return value !== "your_invalid_password";
        },
      )
      .test(
        "invalid-credentials",
        "Invalid Credentials. Please check your ID/Password & try again",
        function (value) {
          return value !== "your_invalid_credentials";
        },
      ),
    remember: Yup.boolean(),
  });

  const formikForgot = useFormik({
    initialValues: {
      employeeId: "",
    },
    validationSchema: Yup.object({
      employeeId: Yup.string().required("Employee ID is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const data = {
        empId: values.employeeId,
      };
      try {
        await forgotPasswordMail(data).unwrap();
        toast("success", "Password reset instructions sent to your email");
        setShowForgotPassword(false);
      } catch (error) {
        toast("error", "Something went wrong!");
      }
      setSubmitting(false);
    },
  });

  const formik = useFormik({
    initialValues: {
      employeeId: "",
      password: "",
      remember: false,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      const submitObj = {
        empId: values.employeeId,
        password: values.password,
        keyExchanger: arrayBufferToBase64(EncryptedData),
      };
      try {
        const result = await axios.post(
          `${process.env.REACT_APP_DEV_API_URL}users/auth/login`,
          submitObj,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        console.log("Login result:", result?.data?.data?.refreshToken);
        const refreshToken = { refreshToken: result?.data?.data?.refreshToken };

        const userDetails = result?.data?.data?.userDetails;
        const updatedData = {
          ...result?.data?.data,
          userDetails: {
            ...result?.data?.data?.userDetails,
            empId: values.employeeId,
          },
        };
        localStorage.setItem("loginDetails", JSON.stringify(updatedData));

        localStorage.setItem("isAuthenticated", true);
        localStorage.setItem("user_email", userDetails?.officialEmail);
        localStorage.setItem("microsoftLogin", "true");

        const passwordUpdateStatus =
          result?.data?.data?.passwordUpdateStatus ??
          userDetails?.passwordUpdateStatus;
        localStorage.setItem(
          "passwordUpdateStatus",
          String(passwordUpdateStatus),
        );

        const localStorageData = JSON.parse(
          localStorage.getItem("loginDetails"),
        );

        dispatch(
          setAuthData({
            token: localStorageData.token,
            refreshToken: localStorageData.refreshToken,
            userDetails: localStorageData.userDetails,
          }),
        );

        scheduleProactiveRefresh();
        const response = await refreshTokenCheck(refreshToken).unwrap();
        toast("success", "Login successful");

        setTimeout(() => {
          if (
            passwordUpdateStatus === false ||
            passwordUpdateStatus === "false"
          ) {
            navigate("/changePasswordStatus");
          } else if (
            result?.data?.data?.userDetails?.roleType === "Prohire Employee"
          ) {
            navigate("/basicDetails");
          } else {
            navigate("/dashboard");
          }
        }, 500);
      } catch (error) {
        toast("error", error?.response?.data.message || "Login failed");
      }
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "employeeId") {
      const newValue = value.toUpperCase();
      const cursorPosition = event.target.selectionStart;

      formik.setFieldValue(name, newValue);

      setTimeout(() => {
        if (event.target.setSelectionRange) {
          event.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {loadingData ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f5f7fa",
            zIndex: 9999,
          }}
        >
          <Loader />
        </Box>
      ) : (
        <GradientBackground>
          <Box
            sx={{
              backgroundImage: `url(${Cover})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              minHeight: "100vh",
              overflow: "hidden",
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100vh"
              sx={{
                minHeight: { xs: "100%", md: "630px" },
                mt: { xs: "20px", md: 0 },
                mb: { xs: "20px", md: 0 },
              }}
            >
              <Grid container columns={16} sx={{ width: "100%", margin: 0 }}>
                <Grid item xs={16} md={8}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      minHeight: { xs: "100%", md: "630px" },
                      mt: { xs: "20px", md: 0 },
                      mb: { xs: "20px", md: 0 },
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: -50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 15,
                      }}
                    >
                      <Box
                        component="img"
                        src={BudgieLogo}
                        alt="Logo"
                        width={268}
                        height={268}
                        sx={{
                          maxWidth: { xs: "80%", md: "60%" },
                          height: "auto",
                        }}
                      />
                    </motion.div>
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={16}
                  md={8}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                    style={{ width: "100%" }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      p={3}
                      boxShadow={3}
                      borderRadius={2}
                      maxWidth="552px"
                      width="100%"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        minHeight: { xs: "100%", md: "550px" },
                        mt: { xs: "20px", md: 0 },
                        mb: { xs: "20px", md: 0 },
                      }}
                    >
                      <Container
                        maxWidth="sm"
                        sx={{
                          width: { xs: "90%", sm: "80%", md: "70%" },
                          px: 0,
                        }}
                      >
                        <motion.div
                          variants={containerVariants}
                          initial="hidden"
                          animate="visible"
                          style={{ width: "100%" }}
                        >
                          <motion.div variants={itemVariants}>
                            <Typography
                              component="h1"
                              variant="h5"
                              sx={{
                                fontSize: { xs: "24px", sm: "36px" },
                                fontWeight: 600,
                                lineHeight: "36px",
                                textAlign: "center",
                                width: "100%",
                                "& span": {
                                  fontSize: { xs: "12px", sm: "14px" },
                                  fontWeight: 400,
                                  lineHeight: "20px",
                                  textAlign: "center",
                                  display: "block",
                                  mt: "8px",
                                },
                              }}
                            >
                              {showForgotPassword
                                ? "Forgot Password"
                                : "Welcome"}
                              <span>
                                {showForgotPassword
                                  ? "Enter the below credentials"
                                  : "Sign in to continue"}
                              </span>
                            </Typography>
                          </motion.div>

                          {!showForgotPassword && (
                            <motion.div variants={itemVariants}>
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1}
                                sx={{ marginTop: "20px", width: "100%" }}
                              >
                                <Button
                                  component={motion.button}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={handleMicrosoftLogin}
                                  disabled={isMicrosoftLogin || isGoogleLogin}
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    lineHeight: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    borderRadius: "4px",
                                    border: "1px solid #E0E0E0",
                                    color: "black",
                                    textTransform: "none",
                                    opacity: isMicrosoftLogin ? 0.6 : 1,
                                    width: "100%",
                                  }}
                                >
                                  {isMicrosoftLogin ? (
                                    <>
                                      <CircularProgress
                                        size={20}
                                        sx={{ marginRight: "8px" }}
                                      />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <Box
                                        component="img"
                                        src={MicrosoftLogo}
                                        alt="Microsoft Logo"
                                        sx={{
                                          marginRight: "8px",
                                          width: { xs: "20px", md: "24px" },
                                          height: { xs: "20px", md: "24px" },
                                        }}
                                      />
                                      Sign in with Microsoft
                                    </>
                                  )}
                                </Button>

                                {/* <div
                                  id="google-signin-btn"
                                  style={{ display: "none" }}
                                /> */}
                                {/* <Button
                                  component={motion.button}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() =>
                                    document
                                      .querySelector(
                                        "#google-signin-btn div[role=button]",
                                      )
                                      ?.click()
                                  }
                                  disabled={isMicrosoftLogin || isGoogleLogin}
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    lineHeight: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    borderRadius: "4px",
                                    border: "1px solid #E0E0E0",
                                    color: "black",
                                    textTransform: "none",
                                    opacity: isGoogleLogin ? 0.6 : 1,
                                    width: "100%",
                                  }}
                                >
                                  {isGoogleLogin ? (
                                    <>
                                      <CircularProgress
                                        size={20}
                                        sx={{ marginRight: "8px" }}
                                      />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <Box
                                        component="img"
                                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                        alt="Google Logo"
                                        sx={{
                                          marginRight: "8px",
                                          width: { xs: "20px", md: "24px" },
                                          height: { xs: "20px", md: "24px" },
                                        }}
                                      />
                                      Sign in with Google
                                    </>
                                  )}
                                </Button> */}
                              </Box>
                            </motion.div>
                          )}

                          {!showForgotPassword && (
                            <motion.div
                              variants={itemVariants}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.4 }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#808080",
                                  mt: 1,
                                }}
                              >
                                <Divider
                                  sx={{
                                    flexGrow: 1,
                                    borderColor: "#E0E0E0",
                                    marginRight: "8px",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    lineHeight: "16px",
                                  }}
                                >
                                  OR
                                </Typography>
                                <Divider
                                  sx={{
                                    flexGrow: 1,
                                    borderColor: "#E0E0E0",
                                    marginLeft: "8px",
                                  }}
                                />
                              </Box>
                            </motion.div>
                          )}

                          <AnimatePresence mode="wait">
                            {!showForgotPassword ? (
                              <motion.div
                                key="login"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                style={{ width: "100%" }}
                              >
                                <form onSubmit={formik.handleSubmit}>
                                  <motion.div variants={itemVariants}>
                                    <TextField
                                      fullWidth
                                      id="employeeId"
                                      placeholder="Emp ID"
                                      name="employeeId"
                                      variant="outlined"
                                      margin="normal"
                                      autoFocus
                                      value={formik.values.employeeId}
                                      onChange={(e) => handleChange(e)}
                                      error={
                                        formik.touched.employeeId &&
                                        Boolean(formik.errors.employeeId)
                                      }
                                      helperText={
                                        formik.touched.employeeId &&
                                        formik.errors.employeeId
                                      }
                                      sx={{ mb: 2 }}
                                    />
                                  </motion.div>

                                  <motion.div variants={itemVariants}>
                                    <TextField
                                      fullWidth
                                      id="password"
                                      name="password"
                                      type={showPassword ? "text" : "password"}
                                      placeholder="Password"
                                      margin="normal"
                                      value={formik.values.password}
                                      onChange={formik.handleChange}
                                      error={
                                        formik.touched.password &&
                                        Boolean(formik.errors.password)
                                      }
                                      helperText={
                                        formik.touched.password &&
                                        formik.errors.password
                                      }
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              aria-label="toggle password visibility"
                                              onClick={
                                                handleTogglePasswordVisibility
                                              }
                                              edge="end"
                                              sx={{ color: "#808080" }}
                                            >
                                              {showPassword ? (
                                                <VisibilityOff />
                                              ) : (
                                                <Visibility />
                                              )}
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{ mb: 2 }}
                                    />
                                  </motion.div>

                                  <motion.div variants={itemVariants}>
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                    >
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            color="primary"
                                            name="remember"
                                            checked={formik.values.remember}
                                            onChange={formik.handleChange}
                                            disabled={isMicrosoftLogin}
                                          />
                                        }
                                        label="Remember me"
                                        sx={{ mb: 2 }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          padding: "10px",
                                          cursor: isMicrosoftLogin
                                            ? "not-allowed"
                                            : "pointer",
                                          textAlign: "right",
                                          color: isMicrosoftLogin
                                            ? "#ccc"
                                            : "#276d21",
                                          pointerEvents: isMicrosoftLogin
                                            ? "none"
                                            : "auto",
                                        }}
                                        onClick={() => {
                                          if (!isMicrosoftLogin) {
                                            formik.resetForm();
                                            formikForgot.resetForm();
                                            setShowForgotPassword(true);
                                          }
                                        }}
                                      >
                                        Forgot Password?
                                      </Typography>
                                    </Box>
                                  </motion.div>

                                  <motion.div variants={itemVariants}>
                                    <Button
                                      type="submit"
                                      fullWidth
                                      variant="contained"
                                      component={motion.button}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      sx={{
                                        borderRadius: (theme) => theme.shape.s,
                                      }}
                                      disabled={
                                        formik.isSubmitting || isMicrosoftLogin
                                      }
                                    >
                                      {formik.isSubmitting
                                        ? "Logging..."
                                        : "Login"}
                                      {formik.isSubmitting && (
                                        <CircularProgress
                                          size={20}
                                          sx={{ color: "green", ml: 1 }}
                                        />
                                      )}
                                    </Button>
                                  </motion.div>
                                </form>
                              </motion.div>
                            ) : (
                              <motion.div
                                key="forgot"
                                variants={slideVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                                style={{ width: "100%" }}
                              >
                                <form onSubmit={formikForgot.handleSubmit}>
                                  <Box sx={{ marginTop: "20px" }}>
                                    <motion.div variants={itemVariants}>
                                      <TextField
                                        fullWidth
                                        name="employeeId"
                                        placeholder="Employee ID"
                                        value={formikForgot.values.employeeId}
                                        onChange={(e) => {
                                          const upperCaseValue =
                                            e.target.value.toUpperCase();
                                          formikForgot.setFieldValue(
                                            "employeeId",
                                            upperCaseValue,
                                          );
                                        }}
                                        error={
                                          formikForgot.touched.employeeId &&
                                          Boolean(
                                            formikForgot.errors.employeeId,
                                          )
                                        }
                                        helperText={
                                          formikForgot.touched.employeeId &&
                                          formikForgot.errors.employeeId
                                        }
                                        sx={{ mb: 2 }}
                                      />
                                    </motion.div>

                                    <motion.div variants={itemVariants}>
                                      <SubmitButtonWithLoader
                                        loading={mailLoading}
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                      >
                                        Submit
                                      </SubmitButtonWithLoader>
                                    </motion.div>

                                    <motion.div variants={itemVariants}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mt: 2,
                                          cursor: "pointer",
                                          textAlign: "center",
                                        }}
                                        onClick={() => {
                                          formik.resetForm();
                                          formikForgot.resetForm();
                                          setShowForgotPassword(false);
                                        }}
                                      >
                                        <ArrowBackIosIcon
                                          sx={{
                                            fontSize: 18,
                                            marginRight: 1,
                                            width: "10px",
                                            height: "10px",
                                          }}
                                        />
                                        Back to Login
                                      </Typography>
                                    </motion.div>

                                    {/* <motion.div variants={itemVariants}>
                                      <Box>
                                        <Footer />
                                      </Box>
                                    </motion.div> */}
                                  </Box>
                                </form>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      </Container>
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </GradientBackground>
      )}
    </>
  );
};

export default Login;
